import { useAuth } from "@clerk/clerk-react";
import { Box, Button, Grid, Skeleton, Typography } from "@mui/material";
import { AlertContext } from "contexts/Alert";
import { OrganizationUserContext } from "contexts/Organization";
import { useContext, useEffect, useState } from "react";
import { TemplateIntakeForm } from "schemas/dashboard";
import { CustomEvent } from "schemas/functions";

import { fetcherAuth } from "utils/api";

import BackgroundForm from "./BackgroundForm";
import TemplateNicheQuestion from "./TemplateNicheQuestion";

const BoughtTemplatesSteps = ["Copywriter Templates", "Your Background"];

interface Props {
  addOnItemId?: number;
  templateIntakeFormId?: number;
  handleNext: () => void;
  showNichePickQuestion?: boolean;
}

const CopywritersFlow = ({
  addOnItemId,
  templateIntakeFormId,
  handleNext,
  showNichePickQuestion,
}: Props) => {
  const { getToken } = useAuth();
  const { currentOrg, profile, organizationVaProfile } = useContext(
    OrganizationUserContext,
  );
  const { setErrorAlert } = useContext(AlertContext);
  const [fetchLoading, setFetchLoading] = useState<boolean>(false);
  const [boughtTemplatesStep, setBoughtTemplatesStep] = useState<number>(0);
  const [templateIntakeForms, setTemplateIntakeForms] = useState<
    TemplateIntakeForm[]
  >([]);

  const backgroundDetails =
    profile?.backgroundDetails || organizationVaProfile?.backgroundDetails;
  const alreadyHasBackground =
    backgroundDetails && Object.keys(backgroundDetails).length > 0;

  const onChange = (e: CustomEvent, index: number) => {
    const { name, value } = e.target;

    setTemplateIntakeForms((prev) => {
      const updatedForms = [...prev];
      if (index >= 0 && index < updatedForms.length) {
        updatedForms[index] = {
          ...updatedForms[index],
          [name]: value,
        };
      }
      return updatedForms;
    });
  };

  const handleSubmit = async (e: CustomEvent) => {
    e.preventDefault();
    if (boughtTemplatesStep === 0) {
      handleSubmitForm();
    } else if (boughtTemplatesStep === 1) {
      storeBackgroundDetails();
    }
  };

  const fetchIntakeForms = async () => {
    try {
      setFetchLoading(true);
      const queryParam = templateIntakeFormId
        ? `templateIntakeFormId=${templateIntakeFormId}`
        : `addOnItemId=${addOnItemId}`;
      const { templateIntakeForms } = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/template-intake-forms?${queryParam}`,
      );
      setTemplateIntakeForms(templateIntakeForms || []);
    } catch (error) {
      setErrorAlert(error);
    } finally {
      setFetchLoading(false);
    }
  };

  const handleSubmitForm = async () => {
    if (alreadyHasBackground) {
      handleNext();
    } else {
      setBoughtTemplatesStep(1);
    }
    try {
      setFetchLoading(true);
      const res = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/template-intake-forms/bulk-update`,
        "PUT",
        {},
        {
          templateIntakeForms,
        },
      );
      setTemplateIntakeForms(res.templateIntakeForms || []);
    } catch (error) {
      setErrorAlert(error);
    } finally {
      setFetchLoading(false);
    }
  };

  const storeBackgroundDetails = async () => {
    handleNext();
    try {
      await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/onboarding`,
        "PUT",
        {},
        {
          ...profile,
        },
      );
    } catch (error) {
      setErrorAlert(error);
    }
  };

  useEffect(() => {
    if (currentOrg?.id && (addOnItemId || templateIntakeFormId)) {
      fetchIntakeForms();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrg?.id, addOnItemId, templateIntakeFormId]);

  return (
    <>
      <Typography variant="h3" gutterBottom sx={{ textAlign: "center", mt: 0 }}>
        {BoughtTemplatesSteps[boughtTemplatesStep]}
      </Typography>

      <form onSubmit={handleSubmit}>
        {fetchLoading ? (
          <Grid>
            <Skeleton animation="wave" width="100%" height="50px" />
            <Skeleton animation="wave" width="100%" height="50px" />
            <Skeleton
              animation="wave"
              width="100%"
              height="400px"
              sx={{ mt: -10, mb: -8 }}
            />
            <Skeleton animation="wave" width="100%" height="80px" />
          </Grid>
        ) : (
          <>
            {boughtTemplatesStep === 0 &&
              templateIntakeForms?.map((form, index) => (
                <TemplateNicheQuestion
                  index={index}
                  onChange={onChange}
                  form={form}
                  key={index}
                  showNichePickQuestion={showNichePickQuestion}
                />
              ))}
            {boughtTemplatesStep === 1 && <BackgroundForm />}
          </>
        )}

        <Grid item xs="auto" container columnGap={1} sx={{ mt: 4 }}>
          <Grid item xs="auto"></Grid>
          <Grid item xs></Grid>
          <Grid item xs="auto">
            <Button disableElevation type="submit">
              Next
              <Box
                sx={{ ml: 1 }}
                component="i"
                className="fa-solid fa-arrow-right"
              />
            </Button>
          </Grid>
        </Grid>
      </form>
    </>
  );
};

export default CopywritersFlow;
